/* Column profiles and drift over the real API. No `X-Lumiq-Tenant` header — hard rule 1.
 *
 * **Two reads and no source access.** `GET /sketches` returns what a past scan measured and
 * `GET /sketch-drift` compares two stored sketches — the response says so itself, in
 * `computed_from`. That property is what makes drift cheap and it is not obvious from a payload of
 * numbers, so the screen renders the platform's own sentence rather than restating it.
 *
 * **`POST /profile` *is* called from here, and the cost is why it is guarded rather than absent.**
 * This paragraph used to say it was not called at all — true when written, and untrue since the
 * panel and `DatasetDetail` grew the action. What has not changed is the cost S-25 records: a
 * profile is 20 full `GROUP BY` scans of a customer's table, nothing server-side bounds concurrent
 * ones, and a double-clicked button buys 40. So every caller disables while pending, and the bulk
 * runner in `catalog-browser` bounds its own concurrency and skips datasets already profiled — see
 * `requestProfile`, which is the one implementation all of them share.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';

import {
  isColumnDrift,
  isColumnSketch,
  isDatasetSketches,
  isSketchDrift,
} from '@/shared/api/generated/catalog';
import type { DatasetSketches, SketchDrift } from '@/shared/api/generated/catalog';
import { forgetIdempotencyKey, getOne, idempotencyKeyFor, post } from '@/shared/api/http';

export interface ProfileRequestResult {
  readonly dataset_id: string;
  readonly dataset_version_id: string;
  readonly columns: readonly string[];
  readonly column_limit: number;
  readonly truncated: boolean;
}

function isProfileRequestResult(value: unknown): value is ProfileRequestResult {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['dataset_id'] === 'string' &&
    typeof candidate['dataset_version_id'] === 'string' &&
    Array.isArray(candidate['columns']) &&
    typeof candidate['column_limit'] === 'number' &&
    typeof candidate['truncated'] === 'boolean'
  );
}

/** One `POST /profile`, as a plain function.
 *
 * **Extracted so the bulk runner and this hook are one implementation.** A hook is bound to one
 * `datasetId` and cannot be called in a loop, so a "run for every dataset" control had the choice
 * of re-writing this request or of calling out to it. Re-writing it is the shape that drifts: the
 * idempotency scope, the guard, and the `finally` that clears the key are three details a second
 * copy gets right on the day it is written and not afterwards.
 *
 * The idempotency key is scoped `catalog.profile` per dataset and cleared in a `finally`, exactly
 * as before — so a retry of a *failed* request reuses the key while a fresh press after a success
 * mints a new one. Nothing about the individual flow changed: `useRequestProfile` below is the same
 * mutation with the same key, the same guard and the same invalidations.
 */
export async function requestProfile(datasetId: string): Promise<ProfileRequestResult> {
  const key = idempotencyKeyFor('catalog.profile', datasetId);
  try {
    return await post(
      `/v1/catalog/datasets/${datasetId}/profile`,
      key,
      isProfileRequestResult,
      'profile request',
    );
  } finally {
    forgetIdempotencyKey('catalog.profile', datasetId);
  }
}

export function useRequestProfile(datasetId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => requestProfile(datasetId),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['catalog', 'sketches', datasetId],
      });
      void queryClient.invalidateQueries({
        queryKey: ['catalog', 'sketch-drift', datasetId],
      });
    },
  });
}

function isSketchesWithColumns(value: unknown): value is DatasetSketches {
  if (!isDatasetSketches(value)) return false;
  return value.columns.every(isColumnSketch);
}

function isDriftWithColumns(value: unknown): value is SketchDrift {
  if (!isSketchDrift(value)) return false;
  return value.columns.every(isColumnDrift);
}

/** The current version's stored sketches, or `null` when the dataset was never profiled.
 *
 * A 404 is a legitimate answer here rather than an error: "never profiled" is the normal state of a
 * freshly harvested dataset, and surfacing it as a failure would put a red alert on every new table.
 * The panel renders it as an invitation instead.
 */
export const PROFILE_POLL_MS = 2000;
/** How often the panel re-reads while a profile is running.
 *
 * Two seconds because a column's `GROUP BY` on a real table takes seconds, so a faster poll would
 * mostly return the same count — and this read is against Lumiq's own database rather than the
 * customer's, so the cost is a cheap indexed count either way. Polling **stops** the moment the
 * count reaches the expected column total, so a finished profile costs nothing.
 */

export function useSketches(
  datasetId: string,
  /** How many columns a complete profile would have, or `undefined` if the caller does not know.
   *
   * **The poll is bounded by this rather than by a timer.** `undefined` means no polling at all,
   * which is the right default: a caller that cannot say what "finished" looks like must not start
   * an interval that never ends. */
  expectedColumns?: number,
): UseQueryResult<DatasetSketches | null> {
  return useQuery({
    queryKey: ['catalog', 'sketches', datasetId],
    /* **Polls only while the count is genuinely short of the total.** `capture` appends and commits
       one column at a time, so this count really does rise as scans complete — which is what makes
       a progress reading a measurement rather than an animation. The interval returns `false` once
       the profile is complete, so React Query stops on its own without anybody clearing a timer. */
    refetchInterval: (query) => {
      if (expectedColumns === undefined || expectedColumns <= 0) return false;
      const stored = query.state.data?.columns.length ?? 0;
      return stored >= expectedColumns ? false : PROFILE_POLL_MS;
    },
    queryFn: async () => {
      try {
        return await getOne(
          `/v1/catalog/datasets/${datasetId}/sketches`,
          isSketchesWithColumns,
          'dataset sketches',
        );
      } catch (failure) {
        if (failure instanceof Error && 'status' in failure && failure.status === 404) {
          return null;
        }
        throw failure;
      }
    },
  });
}

/** Drift against the previous profiled version, or `null` when there is nothing to compare with. */
export function useSketchDrift(datasetId: string): UseQueryResult<SketchDrift | null> {
  return useQuery({
    queryKey: ['catalog', 'sketch-drift', datasetId],
    queryFn: async () => {
      try {
        return await getOne(
          `/v1/catalog/datasets/${datasetId}/sketch-drift`,
          isDriftWithColumns,
          'sketch drift',
        );
      } catch (failure) {
        if (failure instanceof Error && 'status' in failure && failure.status === 404) {
          return null;
        }
        throw failure;
      }
    },
  });
}
